import { getStoredLoginIdentifier } from '../auth/sessionToken';
import { profileStorageKey } from './profileStorageKey';
import {
  mergeProfileVehicles,
  normalizeProfileVehiclesPrimary,
  profileRecordIncludesVehiclesField,
  profileVehicleFromUnknown,
  type ProfileVehicle,
  PROFILE_VEHICLES_MAX,
} from './profileVehicles';

const PROFILE_VEHICLES_PREFIX = 'ofrontend.profileVehicles';

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function resolvedVehiclesAccountId(loginId?: string | null): string | null {
  const id = (loginId ?? getStoredLoginIdentifier())?.trim().toLowerCase();
  return id || null;
}

export function getStoredProfileVehicles(loginId?: string | null): ProfileVehicle[] {
  const account = resolvedVehiclesAccountId(loginId);
  if (!account) {
    return [];
  }
  const raw = safeGet(profileStorageKey(PROFILE_VEHICLES_PREFIX, account));
  if (!raw?.trim()) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return normalizeProfileVehiclesPrimary(
      parsed
        .map((item, index) => profileVehicleFromUnknown(item, index))
        .filter((item): item is ProfileVehicle => item !== null)
        .slice(0, PROFILE_VEHICLES_MAX),
    );
  } catch {
    return [];
  }
}

/** Legacy cache — cleared once the API returns a `vehicles` field. */
export function persistProfileVehicles(
  vehicles: ProfileVehicle[],
  loginId?: string | null,
): void {
  const account = resolvedVehiclesAccountId(loginId);
  if (!account) {
    return;
  }
  const key = profileStorageKey(PROFILE_VEHICLES_PREFIX, account);
  const normalized = normalizeProfileVehiclesPrimary(vehicles.slice(0, PROFILE_VEHICLES_MAX));
  if (normalized.length === 0) {
    safeRemove(key);
    return;
  }
  safeSet(
    key,
    JSON.stringify(
      normalized.map((car) => ({
        id: car.id,
        manufacturer: car.manufacturer,
        model: car.model,
        color: car.color,
        license_number: car.licenseNumber,
        is_primary: car.isPrimary,
      })),
    ),
  );
}

export function clearStoredProfileVehicles(loginId?: string | null): void {
  const account = resolvedVehiclesAccountId(loginId);
  if (!account) {
    return;
  }
  safeRemove(profileStorageKey(PROFILE_VEHICLES_PREFIX, account));
}

/**
 * Prefer GET /profile `vehicles` (including empty). Drop the local cache when the API
 * includes the field so localhost and deployed apps share the same server data.
 * Never fall back to the shared `anonymous` localStorage bucket — that leaked cars across users.
 */
export function resolveProfileVehiclesForSummary(
  record: Record<string, unknown> | null,
  apiVehicles: ProfileVehicle[],
  loginId?: string | null,
): ProfileVehicle[] {
  const apiIncludesField = profileRecordIncludesVehiclesField(record);
  if (apiIncludesField) {
    clearStoredProfileVehicles(loginId);
    return mergeProfileVehicles(apiVehicles, [], true);
  }
  if (!resolvedVehiclesAccountId(loginId)) {
    return [];
  }
  return mergeProfileVehicles(apiVehicles, getStoredProfileVehicles(loginId), false);
}
